import { useRef, useState, useEffect, useCallback } from "react"

export default function usePanningMap() {
  const parkingLotPickerContainerRef = useRef(null)
  const panningMapContainerRef = useRef(null)
  const panningMapInnerRef = useRef(null)
  const panningMapDraggableRef = useRef(null)
  const panningMapInteractiveRef = useRef(null)
  const mobileOverlayRef = useRef(null)
  const mobileDeactivateRef = useRef(null)

  // Enable/disable map movement
  const enabled = useRef(true)
  // Are we currently dragging/panning?
  const interacting = useRef(false)
  // We only want to auto-center the map once
  const initialCenteringDone = useRef(false)

  // Current map scale
  const [innerScale, setInnerScale] = useState(1)

  // Panning references
  const panningOrigin = useRef({ x: 0, y: 0 })
  const innerPositionOrigin = useRef({ x: 0, y: 0 })
  const transformOrigin = useRef({ x: 0, y: 0 })
  const innerPositionNew = useRef({ x: 0, y: 0 })

  // Track window width to detect mobile
  const savedWindowWidth = useRef(0)
  const mobileViewport = useRef(false)

  const [bookingStarterData, setBookingStarterData] = useState(null)

  const offset = useCallback((elem) => {
    if (!elem) {
      return { top: 0, left: 0, right: 0, bottom: 0 }
    }
    const doc = elem.ownerDocument
    if (!doc) return {}
    const docElem = doc.documentElement
    if (!docElem) return {}

    const box = elem.getBoundingClientRect()
    const win = doc.defaultView
    return {
      top: box.top + win.pageYOffset - docElem.clientTop,
      left: box.left + win.pageXOffset - docElem.clientLeft,
      right: box.right + win.pageXOffset - docElem.clientLeft,
      bottom: box.bottom + win.pageYOffset - docElem.clientTop,
    }
  }, [])

  const getCW = useCallback(() => panningMapContainerRef.current?.offsetWidth || 0, [])
  const getCH = useCallback(() => panningMapContainerRef.current?.offsetHeight || 0, [])
  const getDW = useCallback(() => panningMapDraggableRef.current?.offsetWidth || 0, [])
  const getDH = useCallback(() => panningMapDraggableRef.current?.offsetHeight || 0, [])
  const getCO = useCallback(() => offset(panningMapContainerRef.current), [offset])
  const getDO = useCallback(() => offset(panningMapDraggableRef.current), [offset])

  const resetTransformOrigin = useCallback(() => {
    transformOrigin.current = { x: 0, y: 0 }
  }, [])

  const setDraggableStyles = useCallback(() => {
    if (!panningMapDraggableRef.current) return
    const styleEl = panningMapDraggableRef.current

    styleEl.style.transformOrigin = `${transformOrigin.current.x}px ${transformOrigin.current.y}px`

    const transformVal = `translate3d(${innerPositionNew.current.x}px, ${innerPositionNew.current.y}px, 0) scale3d(${innerScale}, ${innerScale}, 1)`
    styleEl.style.transform = transformVal
  }, [innerScale])

  const constrain = useCallback(() => {
    if (innerScale === 1) {
      resetTransformOrigin()
    }
    const CW = getCW()
    const CH = getCH()
    const DW = getDW()
    const DH = getDH()

    const minX = CW - DW * innerScale + transformOrigin.current.x
    const maxX = transformOrigin.current.x
    const minY = CH - DH * innerScale + transformOrigin.current.y
    const maxY = transformOrigin.current.y

    if (innerPositionNew.current.x < minX) {
      innerPositionNew.current.x = minX
    }
    if (innerPositionNew.current.x > maxX) {
      innerPositionNew.current.x = maxX
    }
    if (innerPositionNew.current.y < minY) {
      innerPositionNew.current.y = minY
    }
    if (innerPositionNew.current.y > maxY) {
      innerPositionNew.current.y = maxY
    }

    setDraggableStyles()
  }, [innerScale, resetTransformOrigin, getCW, getCH, getDW, getDH, setDraggableStyles])

  const adjustInteractives = useCallback(() => {
    if (!panningMapInteractiveRef.current) return
    const markerEls = panningMapInteractiveRef.current.querySelectorAll(".map-marker")
    const labelEls = panningMapInteractiveRef.current.querySelectorAll(".map-label")

    const markerTransform = `scale3d(${1 / innerScale}, ${1 / innerScale}, 1)`

    markerEls.forEach((marker) => {
      const { positionstyle, positionx, positiony } = marker.dataset || {}
      marker.style.transform = markerTransform
      if (positionstyle) {
        positionstyle.split(";").forEach((pair) => {
          const [k, v] = pair.split(":").map((s) => s.trim())
          if (k && v) marker.style[k] = v
        })
      }

      marker.classList.remove("map-marker--bottom-right", "map-marker--bottom-left", "map-marker--top-right")

      if (!mobileViewport.current) {
        const xNum = parseFloat(positionx || "0")
        const yNum = parseFloat(positiony || "0")
        if (xNum > 50) {
          if (yNum > 50) {
            marker.classList.add("map-marker--bottom-right")
          } else {
            marker.classList.add("map-marker--top-right")
          }
        } else if (yNum > 50) {
          marker.classList.add("map-marker--bottom-left")
        }
      }
      marker.classList.add("is-ready")
    })

    labelEls.forEach((label) => {
      const { positionstyle } = label.dataset || {}
      label.style.transform = markerTransform
      if (positionstyle) {
        positionstyle.split(";").forEach((pair) => {
          const [k, v] = pair.split(":").map((s) => s.trim())
          if (k && v) label.style[k] = v
        })
      }
    })
  }, [innerScale])

  const focusMapOnMarker = useCallback(() => {
    if (initialCenteringDone.current) return
    const markers = panningMapInteractiveRef.current?.querySelectorAll(".map-marker[data-center=true]") || []
    if (markers.length === 0) {
      initialCenteringDone.current = true
      return
    }
    setTimeout(() => {
      initialCenteringDone.current = true
      panToMarker(markers[0], true)
    }, 20)
  }, [])

  const panToMarker = useCallback(
    (marker, noAnimation) => {
      if (!marker) return
      const markerDims = { w: 60, h: 60 }
      const CW = getCW()
      const CH = getCH()
      const DO = getDO()
      const MO = offset(marker)

      innerPositionNew.current.x = DO.left - MO.left + CW / 2 - markerDims.w / 2
      innerPositionNew.current.y = DO.top - MO.top + CH / 2 - markerDims.h / 2

      if (!noAnimation && panningMapDraggableRef.current) {
        panningMapDraggableRef.current.classList.add("is-animating")
        setTimeout(() => {
          panningMapDraggableRef.current?.classList.remove("is-animating")
        }, 300)
      }

      constrain()
    },
    [constrain, getCW, getCH, getDO, offset],
  )

  const fitMarker = useCallback(
    (marker) => {
      if (!marker) return

      // common dims
      const markerDims = { w: 320, h: 200 }
      const markerMargin = 5
      const CW = getCW()
      const CH = getCH()
      const DO = getDO()
      const CO = getCO()
      const MO = offset(marker)

      // adjust marker offset into container-space
      MO.left -= CO.left
      MO.top -= CO.top

      // ─── MOBILE: always center ─────────────────
      if (mobileViewport.current) {
        innerPositionNew.current.x = DO.left - (CO.left + MO.left - CW / 2 + markerDims.w / 2 - markerMargin)
        innerPositionNew.current.y = DO.top - (CO.top + MO.top - CH / 2 + markerDims.h / 2)

        // animate + clamp
        if (panningMapDraggableRef.current) {
          panningMapDraggableRef.current.classList.add("is-animating")
          setTimeout(() => {
            panningMapDraggableRef.current?.classList.remove("is-animating")
          }, 300)
        }
        return constrain()
      }

      // ─── DESKTOP: measure bubble+popup and apply minimal shift ────────

      const s = innerScale
      const containerRect = panningMapContainerRef.current.getBoundingClientRect()

      // measure pin & popup
      const pinRect = marker.getBoundingClientRect()
      const popup = marker.querySelector(".content")
      if (!popup) return
      const popupRect = popup.getBoundingClientRect()

      // combine their bounds
      const combined = {
        left: Math.min(pinRect.left, popupRect.left),
        top: Math.min(pinRect.top, popupRect.top),
        right: Math.max(pinRect.right, popupRect.right),
        bottom: Math.max(pinRect.bottom, popupRect.bottom),
      }

      // compute overflow with 5px padding
      const PAD = 5
      let dx = 0,
        dy = 0
      if (combined.left < containerRect.left + PAD) dx = containerRect.left + PAD - combined.left
      if (combined.right > containerRect.right - PAD) dx = containerRect.right - PAD - combined.right
      if (combined.top < containerRect.top + PAD) dy = containerRect.top + PAD - combined.top
      if (combined.bottom > containerRect.bottom - PAD) dy = containerRect.bottom - PAD - combined.bottom

      // apply only if needed
      if (dx !== 0 || dy !== 0) {
        innerPositionNew.current.x += dx / s
        innerPositionNew.current.y += dy / s

        if (panningMapDraggableRef.current) {
          panningMapDraggableRef.current.classList.add("is-animating")
          setTimeout(() => {
            panningMapDraggableRef.current?.classList.remove("is-animating")
          }, 300)
        }
        constrain()
      }
    },
    [constrain, getCW, getCH, getDO, getCO, offset, innerScale],
  )

  const onMarkerClick = useCallback(
    (evt) => {
      let target = evt.target
      while (target && !target.classList?.contains("map-marker")) {
        target = target.parentElement
      }
      if (!target) return

      if (target.classList.contains("is-disabled")) return
      target.classList.toggle("is-active")

      const markers = panningMapInteractiveRef.current?.querySelectorAll(".map-marker")
      markers?.forEach((m) => {
        if (m !== target) m.classList.remove("is-active")
      })

      interacting.current = false
      adjustInteractives()
      fitMarker(target)
    },
    [fitMarker],
  )

  const onPointerDown = (evt) => {
    if (!enabled.current) return
    interacting.current = true

    const CO = getCO()
    const DO = getDO()

    panningOrigin.current = { x: evt.pageX, y: evt.pageY }
    innerPositionOrigin.current = { x: DO.left, y: DO.top }

    innerPositionOrigin.current.x -= CO.left
    innerPositionOrigin.current.y -= CO.top
    panningOrigin.current.x -= CO.left
    panningOrigin.current.y -= CO.top
  }

  const onPointerMove = (evt) => {
    if (!enabled.current || !interacting.current) return
    evt.preventDefault()

    const CO = getCO()
    innerPositionNew.current = {
      x: evt.pageX + innerPositionOrigin.current.x + transformOrigin.current.x - panningOrigin.current.x - CO.left,
      y: evt.pageY + innerPositionOrigin.current.y + transformOrigin.current.y - panningOrigin.current.y - CO.top,
    }
    constrain()
  }

  const onPointerUp = () => {
    if (!enabled.current) return
    interacting.current = false
  }

  const mobileActivateClickHandler = useCallback(() => {
    panningMapContainerRef.current?.classList.add("is-fixed")
    enabled.current = true
    mobileOverlayRef.current?.classList.remove("is-active")

    if (mobileDeactivateRef.current) {
      if ("length" in mobileDeactivateRef.current) {
        mobileDeactivateRef.current.forEach((el) => el.classList.add("is-active"))
      } else {
        mobileDeactivateRef.current.classList.add("is-active")
      }
    }

    constrain()
    focusMapOnMarker()
  }, [constrain, focusMapOnMarker])

  const mobileDeactivateClickHandler = useCallback(() => {
    enabled.current = false
    mobileOverlayRef.current?.classList.add("is-active")

    if (mobileDeactivateRef.current) {
      if ("length" in mobileDeactivateRef.current) {
        mobileDeactivateRef.current.forEach((el) => el.classList.remove("is-active"))
      } else {
        mobileDeactivateRef.current.classList.remove("is-active")
      }
    }
    panningMapContainerRef.current?.classList.remove("is-fixed")

    const markers = panningMapInteractiveRef.current?.querySelectorAll(".map-marker")
    markers?.forEach((m) => {
      m.classList.remove("is-active")
    })
  }, [])

  const resizeHandler = useCallback(() => {
    const width = window.innerWidth
    if (savedWindowWidth.current === width) return
    savedWindowWidth.current = width

    mobileViewport.current = width <= 768
    if (width >= 768) {
      enabled.current = true
    }

    const CW = getCW()
    const CH = getCH()
    const DW = getDW()
    const DH = getDH()

    innerPositionNew.current.x = (CW - DW) * 0.34
    innerPositionNew.current.y = (CH - DH) * 0.63

    constrain()
    adjustInteractives()
  }, [adjustInteractives, constrain, getCW, getCH, getDW, getDH])

  useEffect(() => {
    adjustInteractives()
    resizeHandler()
    focusMapOnMarker()

    window.addEventListener("resize", resizeHandler)
    return () => {
      window.removeEventListener("resize", resizeHandler)
    }
  }, [resizeHandler, focusMapOnMarker, adjustInteractives])

  useEffect(() => {
    const innerEl = panningMapInnerRef.current
    if (!innerEl) return

    // Marker clicks
    innerEl.addEventListener("click", onMarkerClick)

    // Pointer events
    innerEl.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("pointermove", onPointerMove)
    document.addEventListener("pointerup", onPointerUp)

    // Cleanup
    return () => {
      innerEl.removeEventListener("click", onMarkerClick)

      innerEl.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("pointermove", onPointerMove)
      document.removeEventListener("pointerup", onPointerUp)
    }
  }, [onMarkerClick])

  return {
    parkingLotPickerContainerRef,
    panningMapContainerRef,
    panningMapInnerRef,
    panningMapDraggableRef,
    panningMapInteractiveRef,
    mobileOverlayRef,
    mobileDeactivateRef,
    mobileActivateClickHandler,
    mobileDeactivateClickHandler,
    adjustInteractives,
    focusMapOnMarker,
    constrain,
    bookingStarterData,
    setBookingStarterData,
  }
}
